import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { VSLSection } from './VSLSection';
import { StatsBar } from './StatsBar';
import { CaseStudies } from './CaseStudies';
import { About } from './About';
import { Deliverables } from './Deliverables';
import { Audience } from './Audience';
import { FinalCTASection } from './FinalCTASection';
import { QualificationForm } from './QualificationForm';
import { BookingCalendar } from './BookingCalendar';
import { trackPageView, trackButtonClick, trackFormStart } from '../utils/analytics';
import { useScrollReveal } from '../hooks/useScrollReveal';

export const SalesPage = () => {
  const [showQualification, setShowQualification] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [qualificationData, setQualificationData] = useState(null);
  const [utmParams, setUtmParams] = useState({ utm_source: '', utm_medium: '', utm_campaign: '' });
  const revealRootRef = useScrollReveal();
  const location = useLocation();
  const navigate = useNavigate();
  // Guard so the auto-open only fires once per landing (survives strict-mode + close).
  const autoOpenedRef = useRef(false);

  useEffect(() => {
    trackPageView();
    const params = new URLSearchParams(location.search);
    // 1. Capture UTM parameters once, at first load.
    setUtmParams({
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
    });
    // 2. Auto-open the qualification form if we're on /reserver or ?book=1.
    const shouldAutoOpen =
      location.pathname === '/reserver' || params.get('book') === '1';
    if (shouldAutoOpen && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      trackFormStart();
      setShowQualification(true);
    }
    // Intentionally run only on mount — UTM capture and auto-open must be one-shot.
  }, []);

  const handleBookingClick = () => {
    trackButtonClick('cta_reserver_appel');
    trackFormStart();
    setShowQualification(true);
  };

  const handleQualified = (data) => {
    // Merge UTM into qualification payload so it travels all the way to the booking API.
    setQualificationData({ ...data, ...utmParams });
    setShowQualification(false);
    setShowBooking(true);
  };

  const handleCloseAll = () => {
    setShowQualification(false);
    setShowBooking(false);
    setQualificationData(null);
    // Clean the URL after close so the form doesn't re-open on the next mount / refresh.
    const params = new URLSearchParams(location.search);
    const onReserverRoute = location.pathname === '/reserver';
    const hasBookParam = params.get('book') === '1';
    if (onReserverRoute || hasBookParam) {
      navigate('/', { replace: true });
    }
  };

  return (
    <div
      ref={revealRootRef}
      data-testid="sales-page"
      className="min-h-screen bg-[#0D1F17] text-[#F5EFE3] overflow-x-hidden"
    >
      {/* ============================
          HERO — hauteur = contenu
          ============================ */}
      <section
        data-testid="hero-section"
        className="px-5 md:px-8"
        style={{ paddingTop: 32, paddingBottom: 40 }}
      >
        <div
          className="mx-auto flex flex-col items-stretch"
          style={{ maxWidth: 420, width: '100%' }}
        >
          {/* H1 */}
          <h1
            className="text-center text-[#F5EFE3]"
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(22px, 6.8vw, 48px)',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            De 0 à 5&nbsp;000€/mois avec les produits digitaux,
            <br />
            <span
              className="font-serif-italic"
              style={{ color: '#8FD4A8', fontWeight: 500, letterSpacing: 0 }}
            >
              en 90&nbsp;jours.
            </span>
          </h1>

          {/* Titre → nouvelle ligne : 12px */}
          <p
            className="text-center mx-auto"
            style={{
              color: 'rgba(245, 239, 227, 0.55)',
              fontSize: 15,
              lineHeight: 1.4,
              maxWidth: 340,
              marginTop: 12,
            }}
          >
            Je t&rsquo;explique ce que personne ne te dit clairement.
          </p>

          {/* Nouvelle ligne → "ÉTAPE 1/2" : 28px */}
          <p
            className="microlabel text-center"
            style={{ marginTop: 28 }}
          >
            Étape 1 / 2
          </p>

          {/* "ÉTAPE 1/2" → vidéo : 12px */}
          <div style={{ marginTop: 12 }}>
            <VSLSection />
          </div>

          {/* Vidéo → bouton : 20px */}
          <button
            onClick={handleBookingClick}
            data-testid="hero-cta-btn"
            className="btn-primary w-full"
            style={{ marginTop: 20 }}
          >
            Réserver mon appel stratégique
          </button>

          {/* Bouton → ligne "30 minutes..." : 10px */}
          <p
            className="text-center"
            style={{
              color: 'rgba(245, 239, 227, 0.45)',
              fontSize: 12,
              marginTop: 10,
            }}
          >
            30 minutes · Google Meet · Sans engagement
          </p>
        </div>
      </section>

      {/* Sections dans l'ordre exact demandé */}
      <StatsBar />
      <CaseStudies />
      <About />
      <Deliverables />
      <Audience />
      <FinalCTASection onCtaClick={handleBookingClick} />

      {/* Footer — ultra-compact */}
      <footer
        data-testid="footer"
        className="border-t border-[#F5EFE3]/10"
        style={{ background: '#0A1912', padding: '20px 20px 24px' }}
      >
        <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <p
            style={{
              color: 'rgba(245, 239, 227, 0.35)',
              fontSize: 10,
              lineHeight: 1.5,
              marginBottom: 10,
              letterSpacing: '0.02em',
            }}
          >
            Les résultats présentés dépendent de l&rsquo;implication et du travail fourni. Ils ne
            constituent en aucun cas une garantie de gains — chaque parcours est individuel.
          </p>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'nowrap',
              marginBottom: 8,
            }}
          >
            <a
              href="/mentions-legales"
              data-testid="footer-mentions-legales"
              style={{
                color: 'rgba(245, 239, 227, 0.5)',
                fontSize: 10,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Mentions légales
            </a>
            <span style={{ color: 'rgba(245, 239, 227, 0.2)', fontSize: 10 }}>·</span>
            <a
              href="/politique-confidentialite"
              data-testid="footer-politique"
              style={{
                color: 'rgba(245, 239, 227, 0.5)',
                fontSize: 10,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Confidentialité
            </a>
            <span style={{ color: 'rgba(245, 239, 227, 0.2)', fontSize: 10 }}>·</span>
            <a
              href="/cgv"
              data-testid="footer-cgv"
              style={{
                color: 'rgba(245, 239, 227, 0.5)',
                fontSize: 10,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              CGV
            </a>
          </div>
          <p
            style={{
              color: 'rgba(245, 239, 227, 0.3)',
              fontSize: 10,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            © {new Date().getFullYear()} · Hustler Digital Academy
          </p>
        </div>
      </footer>

      {/* Modals */}
      {showQualification && (
        <QualificationForm onQualified={handleQualified} onClose={handleCloseAll} />
      )}
      {showBooking && qualificationData && (
        <BookingCalendar
          qualificationData={qualificationData}
          onClose={handleCloseAll}
          onBooked={() => {}}
        />
      )}
    </div>
  );
};

export default SalesPage;
